/**
 * CSRF Token Manager
 * 
 * Handles CSRF token retrieval and storage for HttpOnly cookie scenarios.
 * The token is read from the x-csrf-token response header and stored in memory.
 */

import { API_URL } from '../constants';

class CSRFTokenManager {
  private token: string | null = null;
  private tokenPromise: Promise<string> | null = null;
  private isInitialized = false;

  /**
   * Get the CSRF token. If not cached, fetches it from the server.
   * @returns Promise<string> The CSRF token
   */
  async getToken(): Promise<string> {
    // Return cached token if available
    if (this.token) {
      return this.token;
    }

    // If a fetch is already in progress, wait for it
    if (this.tokenPromise) {
      return this.tokenPromise;
    }

    // Fetch new token
    this.tokenPromise = this.fetchToken();
    try {
      this.token = await this.tokenPromise;
      this.isInitialized = true;
      return this.token;
    } finally {
      this.tokenPromise = null;
    }
  }

  /**
   * Fetch CSRF token from the server via GET request
   * The token is returned in the x-csrf-token response header
   */
  private async fetchToken(): Promise<string> {
    try {
      // Use an existing endpoint that returns a CSRF token in the header (e.g., /api/csrf/admin/me)
      const response = await fetch(`${API_URL}/api/csrf/admin/me`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch CSRF token: ${response.status}`);
      }

      const token = response.headers.get('x-csrf-token');
      
      if (!token) {
        throw new Error('CSRF token not found in response headers');
      }

      return token;
    } catch (error) {
      console.error('Error fetching CSRF token:', error);
      throw error;
    }
  }

  /**
   * Clear the cached token. 
   * Use this when you receive a 403 response indicating the token is invalid.
   */
  clearToken(): void {
    this.token = null;
    this.isInitialized = false;
  }

  /**
   * Check if the token manager has been initialized
   */
  get initialized(): boolean {
    return this.isInitialized;
  }

  /**
   * Initialize the token manager by fetching the token
   * This can be called on app startup to avoid delays on first API call
   */
  async initialize(): Promise<void> {
    await this.getToken();
  }
}

// Export singleton instance
export const csrfManager = new CSRFTokenManager();
